"use client";

import {
    Context,
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
} from "react";
import { SrclangType, VideoInteraction } from "@yedalabs/schema";
import { InteractionStep } from "@/features/storage/sheets/videoSheet/components/tabs/interactions/lib/types";

/**
 * Mode flag distinguishing the two consumers of the shared interaction modal:
 * - `videoSheet`: edits flat list of interactions bound to a video, with
 *   timestamps, persisted via Save Changes.
 * - `lomdaV2`: edits per-gap interactions nested in lomda content, with no
 *   timestamps (engine-determined). Persisted via lomda auto-save.
 */
export type InteractionsMode = "videoSheet" | "lomdaV2";

/**
 * Shape implemented by both the videoSheet and lomdaV2 providers. The shared
 * modal shell + step components consume this context.
 */
export interface InteractionsContextType {
    mode: InteractionsMode;

    // shared state
    interactions: VideoInteraction[];
    selectedInteraction: VideoInteraction | null;
    setInteractionModalOpen: Dispatch<SetStateAction<boolean>>;
    closeModal: () => void;

    // list mutations
    createInteraction: () => void;
    editInteraction: (id: string) => void;
    deleteInteraction: (id: string) => void;
    duplicateInteraction: (id: string) => void;
    getInteractionTitle: (id: string) => string;

    // persistence
    saveChanges: () => Promise<void>;
    loading: boolean;
    changes: boolean;

    // single-interaction edit
    updateInteraction: (
        id: string,
        fieldsToUpdate: Partial<VideoInteraction>
    ) => void;

    // step navigation
    currentStep: InteractionStep;
    nextStep: () => void;
    prevStep: () => void;
    moveToStep: (step: InteractionStep) => void;

    // strategy: each provider supplies the component + label for step 1
    // (videoSheet: time-and-type; lomdaV2: scene-and-type).
    Step1Component: React.FC;
    step1Label: { keyName: string; defaultValue: string };

    // ── videoSheet-only (undefined in lomdaV2 mode) ──
    /** Caption language whose interactions are currently being viewed/edited.
     *  Interactions are stored per language (one set per caption language). */
    selectedSrclang?: SrclangType;
    setSelectedSrclang?: (srclang: SrclangType) => void;
    /** Languages that currently have interactions, for the language picker. */
    availableSrclangs?: SrclangType[];

    // ── lomdaV2-only (undefined in videoSheet mode) ──
    /** Scenes available as targets for the "after which scene" picker. */
    scenes?: { id: string; title: string }[];
    /** Move an interaction to a different scene gap and/or order. */
    setInteractionGap?: (
        interactionId: string,
        gapId: string,
        atOrder: number
    ) => void;
    /** Get the scene id whose after-gap currently hosts this interaction. */
    getInteractionGap?: (interactionId: string) => string | undefined;
    /**
     * Close-only: get the scene id the learner should be rewound to on a
     * wrong answer. `null` / `undefined` = rewind disabled. The render
     * engine resolves this to a real timestamp at render time.
     */
    getLomdaRewindSceneId?: (
        interactionId: string
    ) => string | null | undefined;
    /** Close-only: set the rewind target scene id (or null to disable). */
    setLomdaRewindSceneId?: (
        interactionId: string,
        sceneId: string | null
    ) => void;
}

export const InteractionsContext = createContext<
    InteractionsContextType | undefined
>(undefined);

export const useInteractions = () => {
    const context = useContext(
        InteractionsContext as Context<InteractionsContextType | undefined>
    );
    if (!context) {
        throw new Error(
            "useInteractions must be used within an InteractionsContext"
        );
    }
    return context;
};
